import { ContactDetailsSectionDisplayableEntry } from '@/types/contactDetailsSection';
import { TranslatableFiatAccountType } from '@/types/contacts';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { RecipientResponse } from '@/types/recipient';

export const getRecipientDisplayName = (recipient: RecipientResponse): string => {
  return recipient.individualName ?? recipient.businessName ?? '';
};

export const getRecipientBankName = (recipient: RecipientResponse): string | undefined => {
  return recipient.details.bankName as string | undefined;
};

export const getRecipientAccountNumber = (recipient: RecipientResponse): string | undefined => {
  switch (recipient.type) {
    case PaymentRailType.ACH:
    case PaymentRailType.Wire:
    case PaymentRailType.Swift:
      return (recipient.details.accountNumber ?? recipient.details.iban) as string | undefined;
    case PaymentRailType.PIX:
      return recipient.details.pixKey as string | undefined;
    case PaymentRailType.SPEI:
      return recipient.details.clabe as string | undefined;
    case PaymentRailType.CVU:
      return recipient.details.cvu as string | undefined;
    case PaymentRailType.COP:
      return recipient.details.bankAccount as string | undefined;
    default:
      return undefined;
  }
};

export const getRecipientCurrency = (recipient: RecipientResponse): CurrencyCodeType => {
  return recipient.currency;
};

export const getRecipientDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  switch (recipient.type) {
    case PaymentRailType.Wire:
      return buildWireDisplayEntries(recipient);
    case PaymentRailType.ACH:
      return buildACHDisplayEntries(recipient);
    case PaymentRailType.Swift:
      return buildSwiftDisplayEntries(recipient);
    case PaymentRailType.PIX:
      return buildPixDisplayEntries(recipient);
    case PaymentRailType.COP:
      return buildCOPDisplayEntries(recipient);
    case PaymentRailType.SPEI:
      return buildSpeiDisplayEntries(recipient);
    case PaymentRailType.CVU:
      return buildCVUDisplayEntries(recipient);
    default:
      return [];
  }
};

export const getRecipientAccountTypeLabel = (
  recipient: RecipientResponse,
): TranslatableFiatAccountType => {
  switch (recipient.type) {
    case PaymentRailType.ACH:
      return 'FIAT_ACCOUNT_ACH';
    case PaymentRailType.Wire:
      return 'FIAT_ACCOUNT_WIRE';
    case PaymentRailType.Swift:
      return 'FIAT_ACCOUNT_SWIFT';
    case PaymentRailType.PIX:
      return 'FIAT_ACCOUNT_PIX';
    case PaymentRailType.COP:
      return 'FIAT_ACCOUNT_COP';
    case PaymentRailType.SPEI:
      return 'FIAT_ACCOUNT_SPEI';
    case PaymentRailType.CVU:
      return 'FIAT_ACCOUNT_CVU';
    default:
      return '';
  }
};

const buildWireDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankName = details.bankName as string | undefined;
  const routingNumber = details.routingNumber as string | undefined;
  const accountNumber = details.accountNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankName) {
    entries.push({
      key: 'bankName',
      displayValue: `${bankName} (USD)`,
    });
  }

  if (routingNumber) {
    entries.push({
      key: 'routingNumber',
      label: 'ROUTING_NUMBER_DETAILS',
      displayValue: routingNumber,
    });
  }

  if (accountNumber) {
    entries.push({
      key: 'accountNumber',
      label: 'ACCOUNT_NUMBER_DETAILS',
      displayValue: accountNumber,
    });
  }

  return entries;
};

const buildACHDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankName = details.bankName as string | undefined;
  const routingNumber = details.routingNumber as string | undefined;
  const accountNumber = details.accountNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankName) {
    entries.push({
      key: 'bankName',
      displayValue: `${bankName} (USD)`,
    });
  }

  if (routingNumber) {
    entries.push({
      key: 'routingNumber',
      label: 'ROUTING_NUMBER_DETAILS',
      displayValue: routingNumber,
    });
  }

  if (accountNumber) {
    entries.push({
      key: 'accountNumber',
      label: 'ACCOUNT_NUMBER_DETAILS',
      displayValue: accountNumber,
    });
  }

  return entries;
};

const buildSwiftDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankName = details.bankName as string | undefined;
  const swiftCode = details.swiftCode as string | undefined;
  const iban = details.iban as string | undefined;
  const accountNumber = details.accountNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankName) {
    entries.push({
      key: 'bankName',
      displayValue: `${bankName} (USD)`,
    });
  }

  if (swiftCode) {
    entries.push({
      key: 'swiftCode',
      label: 'SWIFT_CODE',
      displayValue: swiftCode,
    });
  }

  if (iban || accountNumber) {
    entries.push({
      key: 'iban',
      label: iban ? 'IBAN' : 'ACCOUNT_NUMBER',
      displayValue: (iban ?? accountNumber) as string,
    });
  }

  return entries;
};

const buildPixDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const pixKey = details.pixKey as string | undefined;
  const documentNumber = details.documentNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (pixKey) {
    entries.push({
      key: 'pixKey',
      label: 'PIX_KEY',
      displayValue: pixKey,
    });
  }

  if (documentNumber) {
    entries.push({
      key: 'documentNumber',
      label: 'DOCUMENT_NUMBER_CPF_CNPJ',
      displayValue: documentNumber,
    });
  }

  return entries;
};

const buildCOPDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankCode = details.bankCode as string | undefined;
  const accountType = details.accountType as string | undefined;
  const bankAccount = details.bankAccount as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankCode) {
    entries.push({
      key: 'bankCode',
      label: 'BANK_CODE',
      displayValue: bankCode,
    });
  }

  if (accountType) {
    entries.push({
      key: 'accountType',
      label: 'ACCOUNT_TYPE',
      displayValue: accountType,
    });
  }

  if (bankAccount) {
    entries.push({
      key: 'bankAccount',
      label: 'BANK_ACCOUNT',
      displayValue: bankAccount,
    });
  }

  return entries;
};

const buildSpeiDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const clabe = details.clabe as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (clabe) {
    entries.push({
      key: 'clabe',
      label: 'CLABE',
      displayValue: clabe,
    });
  }

  return entries;
};

const buildCVUDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const cvu = details.cvu as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (cvu) {
    entries.push({
      key: 'cvu',
      displayValue: cvu,
    });
  }

  return entries;
};
